// Shared URLs and helpers for the unified Pine59 auth flow. Console hosts the
// screens that don't need a session (get access, password reset, invite,
// Firebase email actions) for both products; Insights keeps its own login and
// links here with `?app=insights` so these screens know where to send people
// back to.

export const CONSOLE_ORIGIN = "https://console.pine59.com"
export const INSIGHTS_ORIGIN = "https://app.pine59.com"

// Every origin Insights has been served from; used to recognise Insights
// links, e.g. the continueUrl in Firebase emails sent by Insights.
const INSIGHTS_ORIGINS = [
  INSIGHTS_ORIGIN,
  "https://insights.pine59.com",
  "https://app.unacast.com",
]

// Origins a redirect may leave Console for.
const TRUSTED_EXTERNAL_ORIGINS = [CONSOLE_ORIGIN, ...INSIGHTS_ORIGINS]

export const BOOK_MEETING_URL = "https://www.pine59.com/get-started"
export const PRIVACY_POLICY_URL = "https://www.pine59.com/privacy"
export const CONTACT_EMAIL = "contact@pine59.com"
export const SUPPORT_EMAIL = "support@pine59.com"

export type AuthApp = "console" | "insights"

export const parseAuthApp = (raw: string | null | undefined): AuthApp =>
  raw === "insights" ? "insights" : "console"

export const appName = (app: AuthApp) =>
  app === "insights" ? "Insights" : "Console"

// None of these links carry the email: URLs end up in server logs, browser
// history and analytics. Screens pass it on in the browser instead (see
// useRememberedEmail).
const withApp = (path: string, app: AuthApp) =>
  app === "insights" ? `${path}?app=insights` : path

// The login screen of the given product. Insights' login lives on its own
// domain, so that one is absolute.
export const loginUrl = (app: AuthApp) =>
  app === "insights" ? `${INSIGHTS_ORIGIN}/login` : "/login"

export const getAccessUrl = (app: AuthApp) => withApp("/get-access", app)

export const forgotPasswordUrl = (app: AuthApp) =>
  withApp("/forgot-password", app)

// "Get added to your company's account" request form.
export const getAddedUrl = (app: AuthApp) => withApp("/get-added", app)

export const CHOOSE_PRODUCT_PATH = "/choose-product"

export type ProductAccess = { console: boolean; insights: boolean }

// Where an email's "Log in" button should go: straight to the one product
// the person has, or to the picker when they have both — or when we don't
// know (lookup failed, no access yet), since the picker works for anyone.
// Relative when the target is on Console, for redirects made by Console.
export const productEntryUrl = (access: ProductAccess | undefined): string => {
  if (access?.console && !access.insights) return loginUrl("console")
  if (access?.insights && !access.console) return loginUrl("insights")
  return CHOOSE_PRODUCT_PATH
}

// Always absolute, for links in emails.
export const productLoginUrl = (access: ProductAccess | undefined): string => {
  const url = productEntryUrl(access)
  return url.startsWith("/") ? `${CONSOLE_ORIGIN}${url}` : url
}

// The invite email's "Finish creating your account" link, which fills in the
// address. It goes after "#", which browsers never send to the server, so it
// stays out of request logs; /signup drops it from the address bar before
// analytics loads. `account` is only displayed.
export const inviteSignupUrl = (email: string, account?: string): string => {
  const query = account ? `?${new URLSearchParams({ account })}` : ""
  return `${CONSOLE_ORIGIN}/signup${query}#${new URLSearchParams({ email })}`
}

export const isInsightsUrl = (url: string | null | undefined): boolean => {
  if (!url) return false
  try {
    return INSIGHTS_ORIGINS.includes(new URL(url).origin)
  } catch {
    return false
  }
}

// Only same-site paths ("/datasets"). Parsed the way browsers parse them, so
// "//host", "/\host" and "/<tab>/host" (all another origin) are rejected.
export const isSafeRelativePath = (path: string): boolean => {
  if (!path.startsWith("/")) return false
  try {
    return new URL(path, CONSOLE_ORIGIN).origin === CONSOLE_ORIGIN
  } catch {
    return false
  }
}

// A continueUrl from a Firebase email link is attacker-controllable (anyone
// can craft the link), so only follow it to Console itself or to Insights.
export const sanitizeContinueUrl = (
  raw: string | null | undefined,
  fallback = "/login",
): string => {
  if (!raw) return fallback
  if (isSafeRelativePath(raw)) return raw
  try {
    const url = new URL(raw)
    if (url.origin === CONSOLE_ORIGIN) {
      // The path alone can still be another site: the path of
      // "https://console.pine59.com//evil.com" is "//evil.com".
      const path = `${url.pathname}${url.search}${url.hash}`
      return isSafeRelativePath(path) ? path : fallback
    }
    if (TRUSTED_EXTERNAL_ORIGINS.includes(url.origin)) return url.toString()
  } catch {
    // not a URL
  }
  return fallback
}
